import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FORMSPREE_CONTACT_URL } from '../constants/site';
import Icon from './Icon';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  country: 'United Kingdom',
  studentAge: '',
  program: 'Noorani Qaida',
  preferredTime: '',
  message: '',
};

const inputClass =
  'bg-surface p-4 rounded-xl border-none font-body-md text-on-surface form-input-focus transition-all w-full';

export default function ContactForm() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (planParam) {
      const label = planParam.charAt(0).toUpperCase() + planParam.slice(1);
      setForm((prev) => ({ ...prev, program: `${label} Plan` }));
    }
  }, [planParam]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const response = await fetch(FORMSPREE_CONTACT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error('Submission failed');
      setStatus('sent');
      setForm(initialForm);
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const isSending = status === 'sending';
  const isSent = status === 'sent';
  const isError = status === 'error';

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="flex flex-col gap-2">
        <label htmlFor="name" className="text-label-md font-bold text-on-surface-variant">
          Full Name
        </label>
        <input
          id="name"
          name="name"
          required
          value={form.name}
          onChange={handleChange}
          className={inputClass}
          placeholder="e.g. Abdullah Khan"
          type="text"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="email" className="text-label-md font-bold text-on-surface-variant">
          Email Address
        </label>
        <input
          id="email"
          name="email"
          required
          value={form.email}
          onChange={handleChange}
          className={inputClass}
          placeholder="email@example.com"
          type="email"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="phone" className="text-label-md font-bold text-on-surface-variant">
          WhatsApp Number
        </label>
        <input
          id="phone"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          className={inputClass}
          placeholder="+1 234 567 890"
          type="tel"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="country" className="text-label-md font-bold text-on-surface-variant">
          Country
        </label>
        <select id="country" name="country" value={form.country} onChange={handleChange} className={inputClass}>
          <option>United Kingdom</option>
          <option>United States</option>
          <option>Canada</option>
          <option>Pakistan</option>
          <option>Other</option>
        </select>
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="studentAge" className="text-label-md font-bold text-on-surface-variant">
          Student Age
        </label>
        <input
          id="studentAge"
          name="studentAge"
          value={form.studentAge}
          onChange={handleChange}
          className={inputClass}
          placeholder="Years"
          type="number"
          min="1"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="program" className="text-label-md font-bold text-on-surface-variant">
          Program Interest
        </label>
        <select id="program" name="program" value={form.program} onChange={handleChange} className={inputClass}>
          <option>Noorani Qaida</option>
          <option>Tajweed</option>
          <option>Hifz Quran</option>
          <option>Islamic Studies</option>
        </select>
      </div>
      <div className="flex flex-col gap-2 md:col-span-2">
        <label htmlFor="preferredTime" className="text-label-md font-bold text-on-surface-variant">
          Preferred Time (GMT)
        </label>
        <input
          id="preferredTime"
          name="preferredTime"
          value={form.preferredTime}
          onChange={handleChange}
          className={inputClass}
          placeholder="e.g. 5:00 PM - 8:00 PM"
          type="text"
        />
      </div>
      <div className="flex flex-col gap-2 md:col-span-2">
        <label htmlFor="message" className="text-label-md font-bold text-on-surface-variant">
          Your Message
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          value={form.message}
          onChange={handleChange}
          className={`${inputClass} resize-none`}
          placeholder="How can we help you?"
        />
      </div>
      <div className="md:col-span-2 pt-4">
        <button
          type="submit"
          disabled={isSending}
          className={`w-full py-5 rounded-full font-label-md font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${
            isSent
              ? 'bg-green-600 text-white'
              : isError
                ? 'bg-red-600 text-white'
                : 'bg-primary text-on-primary hover:shadow-xl'
          } ${isSending ? 'opacity-70 pointer-events-none' : ''}`}
        >
          {isSending && <Icon name="sync" className="animate-spin" />}
          {isSent && <Icon name="check_circle" filled />}
          {isError && <Icon name="error" />}
          {isSending
            ? 'Sending...'
            : isSent
              ? 'Message Sent!'
              : isError
                ? 'Something went wrong — please try again'
                : 'Send Message to Advisor'}
        </button>
      </div>
    </form>
  );
}
